import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * Hooks consommant /api/billing/summary/ — récap calculé par le backend
 * (services.billing_summary.compute_billing_summary).
 *
 * La SPA ne fait AUCUN calcul billing ici : montants, totaux, et règles
 * "N users = N numéros inclus" sont toutes calculées côté backend pour
 * cohérence avec le futur cron de réconciliation Stripe.
 */

export type SubscriptionStatusValue =
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'paused'

export type BillingInterval = 'month' | 'year' | 'unknown'

/** Récap billing tel que sérialisé par le backend (compute_billing_summary). */
export interface BillingSummary {
  plan_slug: string
  plan_name: string
  is_legacy_plan: boolean
  is_enterprise: boolean

  billing_interval: BillingInterval

  users_count: number
  numbers_count: number

  included_numbers: number
  extra_numbers: number
  /** Montants en CAD, string Decimal (ex: "5.00") pour garder la précision. */
  extra_number_unit_price_cad: string
  extra_numbers_monthly_cost_cad: string

  plan_monthly_cost_cad: string
  plan_annual_cost_cad: string

  /** @deprecated calcul backend local, peut diverger de Stripe. Préférer
   *  next_invoice_amount_cad pour le total affiché à l'utilisateur. */
  total_monthly_equivalent_cad: string
  /** @deprecated voir total_monthly_equivalent_cad */
  total_annual_cost_cad: string

  /** Source Stripe (upcoming invoice) : montant exact de la prochaine
   *  facture, incluant pro-rations, discounts, taxes. Null si pas dispo. */
  next_invoice_amount_cad: string | null
  next_invoice_currency: string | null
  /** ISO 8601 (period_end de la prochaine facture Stripe) ou null. */
  next_invoice_date: string | null
  /** Baisse de plan programmée au renouvellement, ou null. */
  pending_change?: {
    plan_slug: string
    plan_name: string
    billing_period: 'monthly' | 'yearly'
    effective_at: string | null
  } | null
  ai_minutes: {
    used_minutes: number
    included_minutes: number | null
    calls_count: number
    period_start: string
    period_end: string
    prepaid_minutes: number
    plan_minutes: number | null
    monthly_packs: { id: number; name: string; minutes: number }[]
    overage_minutes: number
    overage_rate_cad: string | null
    overage_amount_cad: string | null
  } | null

  /** Textos du mois : app + automatisés, plafond, facturation à l'usage. */
  textos: TextosUsage | null

  /** Welcome credit of a company referred by a partner, waiting for the next
   *  invoice (Stripe already takes it off next_invoice_amount_cad). Absent from an older server. */
  welcome_credit?: { amount_cad: string; currency: string } | null

  currency: string
}

export interface TextosUsage {
  used: number
  used_app: number
  used_auto: number
  included: number
  period_start: string
  period_end: string
  usage_billing: boolean
  overage: number
  overage_rate_cad: string
  overage_amount_cad: string
  /** Envois automatisés arrêtés : plafond atteint, facturation éteinte. */
  blocked: boolean
}

export interface BillingSummaryResponse {
  has_subscription: boolean
  subscription_status?: SubscriptionStatusValue
  cancel_at_period_end?: boolean
  current_period_end?: string | null
  trial_end?: string | null
  days_until_trial_end?: number
  summary: BillingSummary | null
  ai_minutes?: BillingSummary['ai_minutes']
  textos?: TextosUsage | null
}

/** Mutation : POST /api/billing/textos/ — le patron active ou éteint la
 *  facturation à l'usage des textos automatisés au-delà du plafond. */
export function useTextosFacturationUsage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data } = await api.post<{ ok: boolean; textos: TextosUsage }>('/api/billing/textos/', { enabled })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-summary'] })
    },
  })
}

/** GET /api/billing/summary/ — récap complet pour la page Billing. */
export function useBillingSummary() {
  return useQuery({
    queryKey: ['billing-summary'],
    queryFn: async () => {
      const { data } = await api.get<BillingSummaryResponse>('/api/billing/summary/')
      return data
    },
    staleTime: 30_000,
  })
}

// =============================================================================
// Preview Stripe d'un changement de plan
// =============================================================================

export interface PreviewChangeLine {
  description: string
  /** Decimal en CAD, ex: "213.00" ou "-21.60" (string pour la précision). */
  amount_cad: string
  kind: 'charge' | 'credit'
  is_proration: boolean
}

export interface PreviewChangePlanResponse {
  /**
   * Si vrai, la subscription actuelle est `cancel_at_period_end=True` (encore
   * active mais planifiée pour annulation). Stripe refuse le preview tant que
   * l'user n'a pas réactivé. Le frontend affiche "Réactive d'abord".
   */
  requires_reactivation?: boolean
  /**
   * Si vrai, soit l'user n'a pas d'abonnement, soit sa subscription Stripe est
   * définitivement `canceled` (fin de période atteinte). Le frontend doit
   * proposer de souscrire au plan cible via checkout, pas un "switch".
   */
  requires_new_subscription?: boolean
  reason?: string
  is_upgrade?: boolean
  /** Trial: any change applies right away and nothing is charged. */
  en_essai?: boolean
  /** Trial: ISO date of the first invoice (end of trial). */
  premiere_facture_le?: string | null
  /** ISO datetime — date d'effet (immédiate ou prochain renouvellement). */
  effect_date?: string | null
  /** Lignes du prélèvement immédiat (proration ancienne + nouvelle période). */
  lines?: PreviewChangeLine[]
  /** Total TTC à prélever maintenant (0 pour downgrade). */
  total_immediate_cad?: string
  /** Proration timestamp of the preview: sent back on apply so the charge matches. */
  proration_date?: number
  currency?: string
  recurring?: {
    amount_cad: string
    interval: 'month' | 'year' | string
    starts_at: string | null
    quantity: number
  }
  target_plan: {
    slug: string
    name: string
  }
}

export interface PreviewChangePlanInput {
  target_plan: string
  billing_period: 'monthly' | 'yearly'
}

/** Mutation : POST /api/billing/preview-change/ — Stripe Invoice.upcoming. */
export function usePreviewChangePlan() {
  return useMutation({
    mutationFn: async (input: PreviewChangePlanInput) => {
      const { data } = await api.post<PreviewChangePlanResponse>(
        '/api/billing/preview-change/',
        input,
      )
      return data
    },
  })
}

// =============================================================================
// Création de Subscription in-app (one-click reactivation)
// =============================================================================

export interface CreateSubscriptionInlineInput {
  target_plan: string
  billing_period: 'monthly' | 'yearly'
}

/**
 * Réponse de /api/billing/create-subscription/.
 *
 * Soit succès direct (CB enregistrée a été prélevée), soit le backend
 * indique qu'il faut basculer sur le flow Checkout (CB manquante, refusée,
 * SCA requis).
 */
export interface CreateSubscriptionInlineResponse {
  success?: boolean
  subscription_id?: string
  plan_slug?: string
  plan_name?: string
  amount_paid_cad?: string
  status?: string
  /** Si true → le frontend doit basculer sur le flow Checkout Stripe. */
  requires_checkout?: boolean
  reason?: 'no_stripe_customer' | 'no_payment_method' | 'card_declined' | 'sca_required' | 'customer_retrieve_failed' | string
  detail?: string
}

/**
 * Hook mutation : POST /api/billing/create-subscription/.
 *
 * Crée une nouvelle Subscription Stripe in-app en utilisant la CB déjà
 * enregistrée. Si Stripe refuse (pas de CB, refus, SCA), la réponse contient
 * `requires_checkout: true` et le frontend doit basculer sur create_checkout_session.
 */
export function useCreateSubscriptionInline() {
  return useMutation({
    mutationFn: async (input: CreateSubscriptionInlineInput) => {
      const { data } = await api.post<CreateSubscriptionInlineResponse>(
        '/api/billing/create-subscription/',
        input,
      )
      return data
    },
  })
}

// =============================================================================
// Apply change plan (upgrade/downgrade sur sub active existante)
// =============================================================================

export interface ApplyChangePlanInput {
  target_plan: string
  billing_period: 'monthly' | 'yearly'
  /** From the preview, so the amount charged is the amount shown. */
  proration_date?: number
}

export interface ApplyChangePlanResponse {
  success?: boolean
  no_change?: boolean
  is_downgrade?: boolean
  plan_slug?: string
  plan_name?: string
  amount_paid_cad?: string
  /** 'immediate' (upgrade) ou 'next_cycle' (downgrade). */
  effect?: 'immediate' | 'next_cycle'
  /** Si true → ouvrir UpdateCardDialog pour MAJ CB puis retry. */
  requires_payment_update?: boolean
  reason?: string
  detail?: string
}

/**
 * POST /api/billing/apply-change/ — applique upgrade/downgrade.
 *
 * Pour les users avec sub active. Pour les sub canceled, utiliser
 * useCreateSubscriptionInline à la place.
 */
export function useApplyChangePlan() {
  return useMutation({
    mutationFn: async (input: ApplyChangePlanInput) => {
      const { data } = await api.post<ApplyChangePlanResponse>(
        '/api/billing/apply-change/',
        input,
      )
      return data
    },
  })
}

/** Annule la baisse de plan programmée : le plan actuel continue. */
export function useCancelPendingChange() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ success: boolean; canceled: boolean }>(
        '/api/billing/cancel-pending-change/',
      )
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing-summary'] }),
  })
}

/** Formate un montant Decimal-as-string en libellé "X CAD" pour l'UI. */
export function formatCad(amount: string | undefined | null): string {
  if (!amount) return '0 CAD'
  const n = Number(amount)
  if (!Number.isFinite(n)) return `${amount} CAD`
  // Locale fr-CA : séparateur de milliers = espace insécable
  return `${n.toLocaleString('fr-CA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} CAD`
}

// =============================================================================
// Packs de minutes IA
// =============================================================================

export interface AiPack {
  slug: string
  name: string
  /** Nom du palier mensuel (Réceptionniste, Plus, Pro) ; vide si pas de mensuel */
  monthly_name: string
  monthly_available: boolean
  /** Un prix annuel existe : avec un forfait annuel, la réceptionniste se paie à l'année */
  yearly_available: boolean
  minutes: number
  target_label: string
  price_cad_monthly: string
  price_cad_onetime: string
  /** Par an, 12 mois moins 20 % ; null sans prix annuel */
  price_cad_yearly: string | null
}

export interface AiMinuteCredit {
  id: number
  pack: string
  name: string
  kind: 'monthly' | 'onetime'
  minutes: number
  is_active: boolean
  created_at: string
}

export interface AiPacksResponse {
  packs: AiPack[]
  credits: AiMinuteCredit[]
  can_subscribe_monthly: boolean
  /** Cycle du forfait en cours */
  billing_interval: 'month' | 'year'
}

export function useAiPacks(enabled = true) {
  return useQuery({
    queryKey: ['ai-packs'],
    queryFn: async () => {
      const { data } = await api.get<AiPacksResponse>('/api/billing/ai-packs/')
      return data
    },
    enabled,
    staleTime: 60_000,
  })
}

export interface BuyAiPackResponse {
  ok?: boolean
  credit?: AiMinuteCredit
  requires_card?: boolean
  requires_action?: boolean
  client_secret?: string
  error?: string
  message?: string
}

export function useBuyAiPack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { pack: string; mode: 'monthly' | 'onetime' }) => {
      const { data } = await api.post<BuyAiPackResponse>('/api/billing/ai-packs/buy/', body)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-summary'] })
      void qc.invalidateQueries({ queryKey: ['ai-packs'] })
    },
  })
}

export function useCancelAiPack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (credit_id: number) => {
      const { data } = await api.post<{ ok: boolean }>('/api/billing/ai-packs/cancel/', { credit_id })
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-summary'] })
      void qc.invalidateQueries({ queryKey: ['ai-packs'] })
    },
  })
}
